<template>
    <div class="profile-wrapper__profile-main-content">
        <template v-if="$ff('profile-revamp')">
            <LazyMicroConsultingSection v-if="sectionVisibility[SectionsVisibilityType.MICRO_CONSULTING]" />
            <LazyProfileRelations v-if="relations" />
            <LazySuggestedForYouSection
                v-if="sectionVisibility[SectionsVisibilityType.SUGGESTED_FOR_YOU]"
                v-show="!showTaskList && showSuggestionsSection"
            />
            <LazyTaskListSection v-if="sectionVisibility[SectionsVisibilityType.TASK_LIST]" v-show="showTaskList" />
            <TranslationSection v-if="isFeatureAvailable" />
            <LazyAboutSectionEdit v-if="isEditionEnabled" />
            <AboutSectionReadOnly v-else />
            <LazyPortfolioSection v-if="sectionVisibility[SectionsVisibilityType.PORTFOLIO_REVAMP] && portfolioRevampEnabled" />
            <ExperiencesSection v-if="sectionVisibility[SectionsVisibilityType.EXPERIENCES]" />
            <PortfolioSectionOld v-if="sectionVisibility[SectionsVisibilityType.PORTFOLIO] && !portfolioRevampEnabled" />
            <AppraisalSection v-if="sectionVisibility[SectionsVisibilityType.APPRAISALS]" />
            <RecommendationsSection v-if="sectionVisibility[SectionsVisibilityType.RECOMMENDATIONS]" />
            <SimilarProfiles v-if="sectionVisibility[SectionsVisibilityType.SIMILAR_PROFILES]" />
            <EducationsSection v-if="sectionVisibility[SectionsVisibilityType.EDUCATIONS]" />
            <CertificationsSection v-if="sectionVisibility[SectionsVisibilityType.CERTIFICATIONS]" />
            <SkillSetSection />
            <ReputationWidgetSection v-if="sectionVisibility[SectionsVisibilityType.EXTERNAL_REPUTATION]" />
            <Categories />
            <LazyProfileProductTourSection v-if="sectionVisibility[SectionsVisibilityType.PROFILE_PRODUCT_TOUR]" />
        </template>
        <template v-else>
            <TranslationSection v-if="isFeatureAvailable" />
            <ProfileRelationsSection v-if="sectionVisibility[SectionsVisibilityType.PROFILE_RELATIONS]" />
            <ClientOnly v-if="isEditionEnabled && profile">
                <LazyTaskCards :firstname="profile.firstname" />
            </ClientOnly>
            <SkillSetSection />
            <IndustryExpertiseSection v-if="sectionVisibility[SectionsVisibilityType.INDUSTRY_EXPERTISE]" />
            <LazyPortfolioSection v-if="sectionVisibility[SectionsVisibilityType.PORTFOLIO_REVAMP] && portfolioRevampEnabled" />
            <PortfolioSectionOld v-if="sectionVisibility[SectionsVisibilityType.PORTFOLIO] && !portfolioRevampEnabled" />
            <ExperiencesSection v-if="sectionVisibility[SectionsVisibilityType.EXPERIENCES]" />
            <AppraisalsSection v-if="sectionVisibility[SectionsVisibilityType.APPRAISALS]" />
            <OldRecommendationsSection v-if="sectionVisibility[SectionsVisibilityType.RECOMMENDATIONS]" />
            <EducationsSection v-if="sectionVisibility[SectionsVisibilityType.EDUCATIONS]" />
            <CertificationsSection v-if="sectionVisibility[SectionsVisibilityType.CERTIFICATIONS]" />
        </template>
    </div>
</template>

<script lang="ts" setup>
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {
        LazyAboutSectionEdit,
        LazyMicroConsultingSection,
        LazyPortfolioSection,
        LazyProfileProductTourSection,
        LazyProfileRelations,
        LazySuggestedForYouSection,
        LazyTaskCards,
        LazyTaskListSection,
    } from '#components';
    import {useProfilePageViewMode} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import AppraisalsSection from '~/components/main/appraisals/AppraisalsSection.vue';
    import IndustryExpertiseSection from '~/components/main/IndustryExpertiseSection.vue';
    import PortfolioSectionOld from '~/components/main/PortfolioSectionOld.vue';
    import ProfileRelationsSection from '~/components/main/ProfileRelationsSection.vue';
    import SkillSetSection from '~/components/main/SkillSetSection.vue';
    import TranslationSection from '~/components/main/TranslationSection.vue';
    import {useProfileSuggestionsVisibility} from '~/composables/useProfileSuggestionsVisibility';
    import AboutSectionReadOnly from '~/features/about/ui/AboutSectionReadOnly.vue';
    import AppraisalSection from '~/features/appraisals/ui/AppraisalSection.vue';
    import Categories from '~/features/categories/Categories.vue';
    import CertificationsSection from '~/features/certifications/ui/CertificationsSection.vue';
    import EducationsSection from '~/features/education/ui/EducationsSection.vue';
    import ExperiencesSection from '~/features/experience/ui/ExperiencesSection.vue';
    import OldRecommendationsSection from '~/features/recommendations/OldRecommendationsSection.vue';
    import RecommendationsSection from '~/features/recommendations/RecommendationsSection.vue';
    import ReputationWidgetSection from '~/features/reputation-widgets/ReputationWidgetSection.vue';
    import SimilarProfiles from '~/features/similar-profiles/ui/SimilarProfiles.vue';
    import {useProfileStore} from '~/store/profileStore';
    import {useSectionsVisibilityStore} from '~/store/sectionsVisibility';
    import {useAsyncTranslationStore} from '~/store/translation/asyncTranslationStore';
    import {SectionsVisibilityType} from '~/types/domain/SectionsVisibility';

    const {profile, relations} = storeToRefs(useProfileStore());
    const {isFeatureAvailable} = storeToRefs(useAsyncTranslationStore());
    const isPortfolioRevampV1Enabled = useFeatureFlag('portfolio-revamp-v1');
    const isWorkshopAiNantesPortfolioRevampEnabled = useFeatureFlag('workshop-ai-nantes-portfolio-revamp');
    const {sectionVisibility} = storeToRefs(useSectionsVisibilityStore());
    const {showTaskList, showSuggestionsSection} = useProfileSuggestionsVisibility();
    const {isEditionEnabled} = useProfilePageViewMode();
    const portfolioRevampEnabled = computed(
        () => isPortfolioRevampV1Enabled.value || (isWorkshopAiNantesPortfolioRevampEnabled.value && isEditionEnabled.value),
    );
</script>
